import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, ArrowRight, CheckCircle, AlertCircle } from "lucide-react";
import api from "../api/axios";

export default function ResetPassword() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");

    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [status, setStatus] = useState("idle"); // idle, submitting, success, error
    const [errorMsg, setErrorMsg] = useState("");

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!token) {
            setErrorMsg("Invalid link. Please try requesting a new one.");
            setStatus("error");
            return;
        }
        if (password !== confirm) {
            setErrorMsg("Passwords do not match.");
            setStatus("error");
            return;
        }
        if (password.length < 8) {
            setErrorMsg("Password must be at least 8 characters.");
            setStatus("error");
            return;
        }

        setStatus("submitting");

        try {
            await api.post("/auth/reset-password", { token, newPassword: password });
            setStatus("success");
            setTimeout(() => navigate("/login"), 3000);
        } catch (err) {
            setErrorMsg(err.response?.data?.error || "Reset failed. Link might be expired.");
            setStatus("error");
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
                <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-xl text-center max-w-sm">
                    <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Invalid Link</h2>
                    <p className="text-gray-500 mb-6">This password reset link is invalid or missing.</p>
                    <Link to="/forgot-password" className="btn-secondary w-full block">Request New Link</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
            <div className="w-full max-w-md">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8"
                >
                    {status === "success" ? (
                        <div className="text-center space-y-4">
                            <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                <CheckCircle className="w-8 h-8" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Password Reset!</h2>
                            <p className="text-gray-600 dark:text-gray-300">
                                Your password has been updated securely. Redirecting to login...
                            </p>
                            <Link to="/login" className="btn-primary w-full block text-center mt-6">
                                Login Now
                            </Link>
                        </div>
                    ) : (
                        <>
                            <div className="mb-8">
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">New Password</h2>
                                <p className="text-gray-500 dark:text-gray-400">
                                    Choose a strong password to secure your journey.
                                </p>
                            </div>

                            {status === "error" && (
                                <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-xl text-sm flex gap-2 items-start">
                                    <AlertCircle className="w-5 h-5 shrink-0" />
                                    {errorMsg}
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        New Password
                                    </label>
                                    <div className="relative">
                                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                                            <Lock className="w-5 h-5" />
                                        </div>
                                        <input
                                            type="password"
                                            required
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className="w-full px-4 py-3 pl-12 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-brand-blue focus:outline-none transition-all"
                                            placeholder="••••••••"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Confirm Password
                                    </label>
                                    <div className="relative">
                                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                                            <Lock className="w-5 h-5" />
                                        </div>
                                        <input
                                            type="password"
                                            required
                                            value={confirm}
                                            onChange={(e) => setConfirm(e.target.value)}
                                            className="w-full px-4 py-3 pl-12 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-brand-blue focus:outline-none transition-all"
                                            placeholder="••••••••"
                                        />
                                    </div>
                                </div>

                                <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1 ml-1">
                                    <p className={password.length >= 8 ? "text-green-600" : ""}>• At least 8 characters</p>
                                    <p className={password === confirm && password.length > 0 ? "text-green-600" : ""}>• Passwords match</p>
                                </div>

                                <button
                                    type="submit"
                                    disabled={status === "submitting"}
                                    className="btn-primary w-full flex items-center justify-center gap-2"
                                >
                                    {status === "submitting" ? "Updating..." : "Reset Password"}
                                    {status !== "submitting" && <ArrowRight className="w-4 h-4" />}
                                </button>
                            </form>
                        </>
                    )}
                </motion.div>
            </div>
        </div>
    );
}
